// components/Outreach/CreateCampaign.js — ТОЛЬКО ДОБАВЛЕНО ПЕРЕКЛЮЧЕНИЕ СЕКЦИЙ
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";
import { HiPlus, HiTrash, HiEye, HiEyeOff } from "react-icons/hi";

const API_BASE = "https://flautreach.com/flautreach/api";

const PLATFORMS = [
  {
    key: "EMAIL", label: "Email", icon: "📧",
    description: "Отправка email-писем", hasSubject: true,
    placeholder: "{Привет|Здравствуйте}, {я нашел|увидел} ваш профиль...",
  },
  {
    key: "FB_MESSENGER", label: "FB Messenger", icon: "💬",
    description: "Сообщения в Messenger", hasSubject: false,
    placeholder: "{Привет|Здравствуйте}! {Понравился|Заинтересовал} ваш контент...",
  },
  {
    key: "CONTACT_FORM", label: "Контактная форма", icon: "📝",
    description: "Отправка через форму", hasSubject: false,
    placeholder: "{Здравствуйте|Добрый день}! {Ищу|Подбираю} специалиста...",
  },
];

// Дефолтные блоки промпта (для режима создания)
const DEFAULT_PROMPT_BLOCKS = [
  {
    id: "scenario",
    label: "🎯🎯 FLAUTREACH OUTREACH SCENARIO",
    content: `== FLAUTREACH OUTREACH SCENARIO ==

GOAL: You are a "client" reaching out to freelance marketers.
The real purpose is to introduce them to Flautreach — an AI tool that finds leads for freelancers.
You want them to (a) become a user, or (b) become an affiliate partner (50% lifetime commission).`
  },
  {
    id: "persona",
    label: "👤 YOUR PERSONA",
    content: `== YOUR PERSONA ==
Role: Works on Flautreach project (SaaS)
Email style: Casual, slightly rushed, like a real startup person
NOT a marketer yourself — you need one`
  },
  {
    id: "message1",
    label: "💬 MESSAGE 1 (initial outreach)",
    content: `== MESSAGE 1 (initial outreach) ==
{Hi | Hello},
I've been following your {work|account} on Instagram and love your approach.
I work on a project in the SaaS space and we are {now|} looking for a {marketing|} expert
to help us scale. Do you have any availability for a new {client|project} {right now|at the moment}?

If so, I'd love to send over the details and discuss a potential collaboration.
Best {regards|wishes}.
{P.S. sorry for using my personal gmail, my work email {is broken|sends to spam}|}`
  },
  {
    id: "message2",
    label: "💬 MESSAGE 2 (after interest)",
    content: `== MESSAGE 2 (after they show interest) ==
Hi, thanks for your message!
We are looking for someone to run Facebook ads for our project — a free AI system that monitors
social networks in search of warm leads for freelancers and agencies.

We have a case study on our blog: https://blog.flautreach.com/2025/12/14/case/
Can we promote this case using Facebook ads? Can you help with this?`
  },
  {
    id: "message3",
    label: "💬 MESSAGE 3 (qualify)",
    content: `== MESSAGE 3 (qualify them / educate) ==
Option A: "It is important for us that our ads expert understands the project correctly.
Can you please tell what the target audience for our project could be?"

Option B: "Do you have experience promoting products like ours?
Or maybe you used something like this in your work?"`
  },
  {
    id: "message4",
    label: "💬 MESSAGE 4 (pivot to referral)",
    content: `== MESSAGE 4 (pivot to referral) ==
Thank you for the information. I will share it with our CMO, and we will get back to you.

Quick follow-up — please ignore if this isn't your thing, but I just remembered another task:
I need to find referral partners. Since you're in the freelance/B2B space, I thought you might be interested.

If you know anyone who wants to get leads using our AI scout, you can earn significant residual income.
We pay a straight 50% lifetime commission for every referred client.
Check the program if interested: http://partners.flautreach.com/`
  },
  {
    id: "qa",
    label: "❓ EXPECTED Q&A",
    content: `== EXPECTED Q&A ==

Q: What is Flautreach?
A: It's a free AI system that monitors social networks (Reddit, Twitter, LinkedIn, etc.)
and alerts freelancers/agencies when someone is looking for their specific services.
Think of it as a 24/7 scout finding warm inbound leads.

Q: How does it work?
A: You set up keywords related to your services, Flautreach scans social media,
and sends you alerts when someone is actively looking. Blog case: https://blog.flautreach.com/2025/12/14/case/

Q: Is it really free?
A: Yes, basic version is free. We have premium plans for more volume/channels.

Q: Who is the target audience?
A: Freelancers (designers, developers, copywriters, marketers, etc.) and small agencies
who need a consistent flow of new clients without cold outreach.

Q: What's our budget for ads?
A: We're flexible, want to start with testing first. Let's discuss once you understand the project.

Q: What results do you expect?
A: Mainly awareness and sign-ups from freelancers. The case study shows what users achieve.

Q: Referral program details?
A: 50% lifetime commission. If someone you refer pays $50/month, you get $25/month forever.
Sign up: http://partners.flautreach.com/`
  },
  {
    id: "tone",
    label: "🎭 TONE GUIDELINES",
    content: `== TONE GUIDELINES ==
- Casual but professional
- Not pushy — let them lead
- Genuine curiosity about their work
- Slight impatience (startup pace) is ok
- Never reveal this is automated`
  },
];

// ═══════════════════════════════════════════════════════════
// ФУНКЦИИ ПАРСИНГА ПРОМПТА
// ═══════════════════════════════════════════════════════════

/**
 * Разбивает цельный промпт на блоки по заголовкам == ... ==
 */
function parsePromptToBlocks(fullPrompt) {
  if (!fullPrompt || !fullPrompt.trim()) return [];
  
  // Ищем все заголовки == ... ==
  const headerRegex = /^==\s*(.+?)\s*==$/gm;
  const headers = [];
  let match;
  
  while ((match = headerRegex.exec(fullPrompt)) !== null) {
    headers.push({
      title: match[1].trim(),
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }
  
  if (headers.length === 0) {
    // Нет заголовков — возвращаем весь текст как один блок
    return [{
      id: "block_1",
      label: "Блок 1",
      content: fullPrompt.trim(),
    }];
  }
  
  // Разбиваем на блоки: от заголовка до следующего заголовка (или конца)
  const blocks = [];
  
  for (let i = 0; i < headers.length; i++) {
    const current = headers[i];
    const next = headers[i + 1];
    
    const contentStart = current.endIndex;
    const contentEnd = next ? next.startIndex : fullPrompt.length;
    
    let content = fullPrompt.substring(contentStart, contentEnd).trim();
    
    blocks.push({
      id: `parsed_block_${i}_${Date.now()}`,
      label: current.title,
      content: `== ${current.title} ==\n${content}`,
    });
  }
  
  return blocks;
}

/**
 * Собирает блоки обратно в цельный промпт
 */
function buildPromptFromBlocks(blocks) {
  return blocks
    .map(b => b.content.trim())
    .filter(Boolean)
    .join("\n\n");
}


// ═══════════════════════════════════════════════════════════
// КОМПОНЕНТ
// ═══════════════════════════════════════════════════════════

export function CreateCampaign({ onBack, onCreated, add, editCampaign = null, duplicateCampaign = null }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const isEditing = !!editCampaign;
  const isDuplicating = !!duplicateCampaign;
  
  // Определяем исходные данные (для дублирования или редактирования)
  const sourceData = duplicateCampaign || editCampaign;
  
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  
  // Для дублирования добавляем "(копия)" к имени
  const [campaignName, setCampaignName] = useState(
    isDuplicating ? `${sourceData?.name || ""} (копия)` : (sourceData?.name || "")
  );
  const [campaignDesc, setCampaignDesc] = useState(sourceData?.description || "");
  
  const [selectedPlatforms, setSelectedPlatforms] = useState(sourceData?.platforms || []);
  const [messages, setMessages] = useState(sourceData?.message_templates || {});
  
  const [promptBlocks, setPromptBlocks] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [createIntentionsNow, setCreateIntentionsNow] = useState(false);
  const [taskInfo, setTaskInfo] = useState(null);
  const [loadingTaskInfo, setLoadingTaskInfo] = useState(false);

  // ═══ ИНИЦИАЛИЗАЦИЯ БЛОКОВ ПРОМПТА ═══
  useEffect(() => {
    if (isEditing || isDuplicating) {
      const existingPrompt = sourceData?.dialog_prompt || "";
      
      if (existingPrompt.trim()) {
        // Парсим существующий промпт по заголовкам == ... ==
        const blocks = parsePromptToBlocks(existingPrompt);
        setPromptBlocks(blocks.length > 0 ? blocks : [{ id: "empty_1", label: "Блок 1", content: existingPrompt }]);
      } else {
        // Промпта нет — пустой блок
        setPromptBlocks([{ id: "empty_1", label: "Блок 1", content: "" }]);
      }
    } else {
      // Режим создания — дефолтные блоки
      setPromptBlocks(DEFAULT_PROMPT_BLOCKS.map(b => ({ ...b })));
    }
  }, [sourceData?.id, isEditing, isDuplicating]);

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    setLoadingTasks(true);
    try {
      const resp = await fetch(`${API_BASE}/tasks/`, { headers: getHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTasks(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      add?.("Ошибка загрузки задач", "error");
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (!selectedTaskId || !createIntentionsNow) { setTaskInfo(null); return; }
    loadTaskInfo(selectedTaskId);
  }, [selectedTaskId, createIntentionsNow]);

  const loadTaskInfo = async (taskId) => {
    setLoadingTaskInfo(true);
    const task = tasks.find((t) => t.id === taskId);
    const taskType = task?.task_type || "scraping";
    try {
      let totalResults = 0;
      if (taskType === "filtering" || taskType === "scraping_filtering") {
        const resp = await fetch(
          `${API_BASE}/tasks/${taskId}/filtering-results/?regular_filter=passed&page_size=1`,
          { headers: getHeaders() }
        );
        if (resp.ok) {
          const data = await resp.json();
          totalResults = data.pagination?.total || data.summary?.total_runs || 0;
        }
      } else {
        const resp = await fetch(
          `${API_BASE}/tasks/${taskId}/results/?status=success&page_size=1`,
          { headers: getHeaders() }
        );
        if (resp.ok) {
          const data = await resp.json();
          totalResults = data.pagination?.total || 0;
        }
      }
      setTaskInfo({ name: task?.name || `#${taskId}`, task_type: taskType, total_results: totalResults });
    } catch (err) {
      add?.("Ошибка загрузки инфо о задаче", "error");
    } finally {
      setLoadingTaskInfo(false);
    }
  };

  // ─── Управление блоками ───
  const addPromptBlock = () => {
    const newId = `block_${Date.now()}`;
    setPromptBlocks(prev => [...prev, { id: newId, label: `Блок ${prev.length + 1}`, content: "" }]);
  };

  const removePromptBlock = (id) => {
    setPromptBlocks(prev => {
      const filtered = prev.filter(b => b.id !== id);
      return filtered.length > 0 ? filtered : [{ id: "empty_1", label: "Блок 1", content: "" }];
    });
  };

  const updatePromptBlock = (id, field, value) => {
    setPromptBlocks(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  // ─── Сообщения ───
  const togglePlatform = (key) => {
    setSelectedPlatforms((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
    if (!messages[key]) setMessages((prev) => ({ ...prev, [key]: { subject: "", body: "" } }));
  };

  const updateMessage = (platform, field, value) => {
    setMessages((prev) => ({ ...prev, [platform]: { ...prev, [platform]: { ...prev[platform], [field]: value } } }));
  };

  // ─── Валидация ───
  const canProceed = () => {
    if (step === 1) {
      return campaignName.trim().length > 0 && promptBlocks.some(b => b.content.trim());
    }
    if (step === 2) {
      if (!selectedPlatforms.length) return false;
      return selectedPlatforms.every((p) => {
        const msg = messages[p];
        if (!msg) return false;
        if (PLATFORMS.find((pl) => pl.key === p)?.hasSubject && !msg.subject?.trim()) return false;
        return msg.body?.trim().length > 0;
      });
    }
    return true;
  };

  // ─── СОХРАНЕНИЕ ───
  const handleSubmit = async () => {
    if (!campaignName.trim()) return;
    setCreating(true);
    try {
      const payload = {
        name: campaignName.trim(),
        description: campaignDesc.trim(),
        platforms: selectedPlatforms,
        message_templates: messages,
        dialog_prompt: buildPromptFromBlocks(promptBlocks),
      };

      if (!isEditing && createIntentionsNow && selectedTaskId && taskInfo?.total_results > 0) {
        payload.source_task_id = selectedTaskId;
      }

      // Та же логика URL и method как в оригинале
      const url = isEditing
        ? `${API_BASE}/outreach/campaigns/${editCampaign.id}/`
        : `${API_BASE}/outreach/campaigns/create_from_task/`;
      
      const method = isEditing ? "PATCH" : "POST";

      const resp = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || JSON.stringify(err) || "Ошибка");
      }

      const campaign = await resp.json();
      
      if (isEditing) {
        add?.("Кампания обновлена!", "success");
      } else if (isDuplicating) {
        add?.("Кампания успешно скопирована!", "success");
      } else if (campaign.intentions_created > 0) {
        add?.(`Кампания создана! ${campaign.intentions_created} намерений`, "success");
      } else {
        add?.("Кампания создана (без намерений)", "success");
      }
      
      onCreated?.(campaign);
    } catch (err) {
      add?.(err.message || "Ошибка", "error");
    } finally {
      setCreating(false);
    }
  };

  const steps = isEditing 
    ? ["Промпт и название", "Сообщения"] 
    : isDuplicating
      ? ["Промпт и название", "Сообщения"]
      : ["Промпт и название", "Сообщения", "Задача-источник"];

  const fullPromptText = buildPromptFromBlocks(promptBlocks);

  // Заголовок в зависимости от режима
  const getTitle = () => {
    if (isEditing) return "✏️ Редактирование кампании";
    if (isDuplicating) return "📋 Копирование кампании";
    return "Создание аутрич-кампании";
  };

  // ══════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════
  return (
    <div className="outreach-create-wrap">
      <div className="rules-header">
        <div>
          <h2>{getTitle()}</h2>
          <p>Шаг {step} из {steps.length}</p>
        </div>
        <button className="btn-cancel" onClick={onBack}>← Назад</button>
      </div>

      {/* ПЕРЕКЛЮЧАТЕЛЬ СЕКЦИЙ (ДОБАВЛЕНО) */}
      <div style={{
        display: "flex",
        gap: 4,
        marginBottom: 24,
        background: "#f3f4f6",
        padding: 4,
        borderRadius: 12,
        flexWrap: "wrap",
      }}>
        <button
          onClick={() => setStep(1)}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            background: step === 1 ? "#fff" : "transparent",
            color: step === 1 ? "#1f2937" : "#6b7280",
            boxShadow: step === 1 ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>📋</span> Промпт и название
        </button>
        <button
          onClick={() => setStep(2)}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            background: step === 2 ? "#fff" : "transparent",
            color: step === 2 ? "#1f2937" : "#6b7280",
            boxShadow: step === 2 ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>💬</span> Сообщения
        </button>
        {!isEditing && !isDuplicating && (
          <button
            onClick={() => setStep(3)}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              background: step === 3 ? "#fff" : "transparent",
              color: step === 3 ? "#1f2937" : "#6b7280",
              boxShadow: step === 3 ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>🎯</span> Задача-источник
          </button>
        )}
      </div>

      {/* ═══ STEP 1: ПРОМПТ + НАЗВАНИЕ ═══ */}
      {step === 1 && (
        <div>
          <div className="section-card" style={{ marginBottom: 16 }}>
            <h3>📋 Название и описание</h3>
            <div className="form-group">
              <label className="form-label">Название *</label>
              <input className="form-input" style={{ paddingLeft: 14 }} value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Например: Рассылка маркетологам v2" />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea className="intent-textarea" style={{ minHeight: 60 }} value={campaignDesc} onChange={e => setCampaignDesc(e.target.value)} placeholder="Краткое описание..." />
            </div>
          </div>

          {/* Промпт */}
          <div className="section-card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}>🤖 Промпт для обработки диалога *</h3>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4, marginBottom: 0 }}>
                  Блоки разделены заголовками <code style={{ background: "#e5e7eb", padding: "1px 6px", borderRadius: 4 }}>== Заголовок ==</code>. При сохранении собираются через двойной перевод строки.
                </p>
              </div>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="btn-toggle"
                style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
              >
                {showPreview ? <HiEyeOff size={16} /> : <HiEye size={16} />}
                {showPreview ? "Скрыть" : "Предпросмотр"}
              </button>
            </div>

            {/* Предпросмотр */}
            {showPreview && (
              <div style={{
                marginBottom: 20, padding: "16px 20px",
                background: "#1e1e2e", color: "#cdd6f4",
                borderRadius: 10, fontSize: 12,
                fontFamily: "monospace", whiteSpace: "pre-wrap",
                maxHeight: 400, overflow: "auto",
                border: "2px solid #313244",
              }}>
                <div style={{ color: "#89b4fa", fontWeight: 700, marginBottom: 8, fontSize: 11 }}>
                  Предпросмотр ({promptBlocks.length} блоков, {fullPromptText.length} симв.)
                </div>
                {fullPromptText || <span style={{ color: "#6c7086" }}>Промпт пуст...</span>}
              </div>
            )}

            {/* Блоки */}
            <div style={{ display: "grid", gap: 12 }}>
              {promptBlocks.map((block, idx) => (
                <div key={block.id} style={{ border: "1.5px solid var(--border)", borderRadius: 10, background: "#fafbff", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f0f4ff", borderBottom: "1px solid var(--border)" }}>
                    <input
                      value={block.label}
                      onChange={e => updatePromptBlock(block.id, "label", e.target.value)}
                      style={{ flex: 1, border: "none", background: "transparent", fontSize: 13, fontWeight: 600, outline: "none", color: "var(--dark)" }}
                      placeholder="Название блока"
                    />
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>Блок {idx + 1}</span>
                    <button onClick={() => removePromptBlock(block.id)} style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title="Удалить">
                      <HiTrash size={14} />
                    </button>
                  </div>
                  <textarea
                    value={block.content}
                    onChange={e => updatePromptBlock(block.id, "content", e.target.value)}
                    style={{ width: "100%", minHeight: 360, padding: "12px 14px", border: "none", fontSize: 12, fontFamily: "monospace", resize: "vertical", outline: "none", background: "#fff", color: "#1e1e2e" }}
                    placeholder={`== Заголовок ==\nСодержимое...`}
                  />
                </div>
              ))}
            </div>

            <button onClick={addPromptBlock} style={{
              marginTop: 12, width: "100%", padding: "12px", borderRadius: 8,
              border: "2px dashed #d1d5db", background: "#f9fafb", color: "#6b7280",
              cursor: "pointer", fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <HiPlus size={18} /> Добавить блок
            </button>
          </div>

          <div className="form-actions">
            <button className="btn-primary" style={{ width: "auto", padding: "13px 40px" }} disabled={!canProceed()} onClick={() => setStep(2)}>
              Далее → Сообщения
            </button>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: СООБЩЕНИЯ С ВАРИАНТАМИ ═══ */}
      {step === 2 && (
        <div className="section-card">
          <h3>💬 Первые сообщения (варианты)</h3>
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 20 }}>
            Каждый вариант — отдельное поле. При сохранении они склеиваются автоматически.
            Используйте <code style={{ background: "#e5e7eb", padding: "1px 6px", borderRadius: 4 }}>{`{вариант1|вариант2}`}</code> внутри текста.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
            {PLATFORMS.map(p => (
              <button key={p.key} onClick={() => togglePlatform(p.key)} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 10,
                border: selectedPlatforms.includes(p.key) ? "2px solid #3b82f6" : "1.5px solid var(--border)",
                background: selectedPlatforms.includes(p.key) ? "#eff6ff" : "#fff",
                cursor: "pointer", fontWeight: selectedPlatforms.includes(p.key) ? 700 : 500, fontSize: 14,
              }}>
                <span style={{ fontSize: 18 }}>{p.icon}</span>
                <span>{p.label}</span>
                {selectedPlatforms.includes(p.key) && <span style={{ color: "#3b82f6", fontWeight: 700, fontSize: 14 }}>✓</span>}
              </button>
            ))}
          </div>

          {selectedPlatforms.map(pKey => {
            const plat = PLATFORMS.find(p => p.key === pKey);
            const msg = messages[pKey] || { subject: "", body: "" };

            // Единый разделитель (такой же, как при склеивании)
            const DELIMITER = "\n—-----\n";

            // ============= BODY =============
            const rawBody = msg.body || "";
            const bodyParts = rawBody === "" ? [""] : rawBody.split(DELIMITER);

            const handleBodyChange = (index, newValue) => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curBody = cur.body || "";
                const parts = curBody === "" ? [""] : curBody.split(DELIMITER);
                parts[index] = newValue;
                return { ...prev, [pKey]: { ...cur, body: parts.join(DELIMITER) } };
              });
            };

            const handleAddBody = () => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curBody = cur.body || "";
                const newBody = curBody === "" ? DELIMITER : curBody + DELIMITER;
                return { ...prev, [pKey]: { ...cur, body: newBody } };
              });
            };

            const handleRemoveBody = (index) => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curBody = cur.body || "";
                const parts = curBody === "" ? [""] : curBody.split(DELIMITER);
                parts.splice(index, 1);
                if (parts.length === 0) parts.push("");
                return { ...prev, [pKey]: { ...cur, body: parts.join(DELIMITER) } };
              });
            };

            // ============= SUBJECT (только EMAIL) =============
            const rawSubject = msg.subject || "";
            const subjectParts = plat.hasSubject ? (rawSubject === "" ? [""] : rawSubject.split(DELIMITER)) : [];

            const handleSubjectChange = (index, newValue) => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curSubj = cur.subject || "";
                const parts = curSubj === "" ? [""] : curSubj.split(DELIMITER);
                parts[index] = newValue;
                return { ...prev, [pKey]: { ...cur, subject: parts.join(DELIMITER) } };
              });
            };

            const handleAddSubject = () => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curSubj = cur.subject || "";
                const newSubj = curSubj === "" ? DELIMITER : curSubj + DELIMITER;
                return { ...prev, [pKey]: { ...cur, subject: newSubj } };
              });
            };

            const handleRemoveSubject = (index) => {
              setMessages(prev => {
                const cur = prev[pKey] || { subject: "", body: "" };
                const curSubj = cur.subject || "";
                const parts = curSubj === "" ? [""] : curSubj.split(DELIMITER);
                parts.splice(index, 1);
                if (parts.length === 0) parts.push("");
                return { ...prev, [pKey]: { ...cur, subject: parts.join(DELIMITER) } };
              });
            };

            return (
              <div key={pKey} style={{
                marginBottom: 24, padding: "24px", borderRadius: 14,
                border: "1.5px solid #bfdbfe", background: "#f8faff",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h4 style={{ margin: 0, fontSize: 16 }}>{plat.icon} {plat.label}</h4>
                  <button onClick={() => togglePlatform(pKey)} style={{
                    padding: "6px 14px", fontSize: 12, borderRadius: 6,
                    border: "1px solid #fecaca", background: "#fef2f2",
                    color: "#dc2626", cursor: "pointer",
                  }}>Убрать</button>
                </div>

                {/* SUBJECT VARIANTS (EMAIL only) */}
                {plat.hasSubject && (
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <label className="form-label" style={{ margin: 0, fontSize: 14 }}>
                        📌 Тема <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: 12 }}>({subjectParts.length} вар.)</span>
                      </label>
                      <button onClick={handleAddSubject} style={{
                        padding: "6px 16px", fontSize: 12, borderRadius: 8,
                        border: "1px solid #bbf7d0", background: "#f0fdf4",
                        color: "#16a34a", cursor: "pointer", fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                        <HiPlus size={14} /> Добавить вариант
                      </button>
                    </div>

                    {subjectParts.map((text, idx) => (
                      <div key={`${pKey}-subj-${idx}`} style={{
                        marginBottom: idx < subjectParts.length - 1 ? 16 : 0,
                        padding: "16px", background: "#fff",
                        borderRadius: 10, border: "1px solid #e5e7eb",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700, color: "#6b7280",
                            background: "#e5e7eb", padding: "3px 10px", borderRadius: 6,
                          }}>
                            Вариант {idx + 1}
                          </span>
                          {subjectParts.length > 1 && (
                            <button onClick={() => handleRemoveSubject(idx)} style={{
                              width: 28, height: 28, borderRadius: 6,
                              border: "1px solid #fecaca", background: "#fef2f2",
                              color: "#dc2626", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <HiTrash size={12} />
                            </button>
                          )}
                        </div>
                        <input
                          className="form-input"
                          style={{ width: "100%", padding: "10px 14px", fontSize: 13 }}
                          value={text}
                          onChange={e => handleSubjectChange(idx, e.target.value)}
                          placeholder={`Тема варианта ${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* BODY VARIANTS */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <label className="form-label" style={{ margin: 0, fontSize: 14 }}>
                      💬 Текст <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: 12 }}>({bodyParts.length} вар.)</span>
                    </label>
                    <button onClick={handleAddBody} style={{
                      padding: "6px 16px", fontSize: 12, borderRadius: 8,
                      border: "1px solid #bbf7d0", background: "#f0fdf4",
                      color: "#16a34a", cursor: "pointer", fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      <HiPlus size={14} /> Добавить вариант
                    </button>
                  </div>

                  {bodyParts.map((text, idx) => (
                    <div key={`${pKey}-body-${idx}`} style={{
                      marginBottom: idx < bodyParts.length - 1 ? 16 : 0,
                      padding: "16px", background: "#fff",
                      borderRadius: 10, border: "1px solid #e5e7eb",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <span style={{
                          fontSize: 12, fontWeight: 700, color: "#1d4ed8",
                          background: "#dbeafe", padding: "3px 10px", borderRadius: 6,
                        }}>
                          Вариант {idx + 1}
                        </span>
                        {bodyParts.length > 1 && (
                          <button onClick={() => handleRemoveBody(idx)} style={{
                            width: 28, height: 28, borderRadius: 6,
                            border: "1px solid #fecaca", background: "#fef2f2",
                            color: "#dc2626", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <HiTrash size={12} />
                          </button>
                        )}
                      </div>
                      <textarea
                        className="intent-textarea"
                        style={{
                          width: "100%",
                          minHeight: 200,
                          padding: "14px 16px",
                          fontSize: 13,
                          lineHeight: 1.7,
                        }}
                        value={text}
                        onChange={e => handleBodyChange(idx, e.target.value)}
                        placeholder={`Текст варианта ${idx + 1}\n\nПоддерживает {вариативность|варианты} внутри текста`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {selectedPlatforms.length === 0 && (
            <div style={{
              padding: "60px 20px", textAlign: "center",
              color: "#9ca3af", border: "2px dashed #d1d5db",
              borderRadius: 12,
            }}>
              <p style={{ fontSize: 18, marginBottom: 8 }}>📭 Выберите платформы</p>
              <p style={{ fontSize: 14 }}>Нажмите на платформу выше, чтобы добавить варианты сообщений</p>
            </div>
          )}

          <div className="form-actions" style={{ gap: 12, marginTop: 28 }}>
            <button className="btn-cancel" onClick={() => setStep(1)}>← Назад</button>
            {isEditing ? (
              <button className="btn-primary" style={{ width: "auto", padding: "13px 40px" }} disabled={creating || !canProceed()} onClick={handleSubmit}>
                {creating ? "⏳ Сохранение..." : "💾 Сохранить кампанию"}
              </button>
            ) : isDuplicating ? (
              <button className="btn-primary" style={{ width: "auto", padding: "13px 40px" }} disabled={creating || !canProceed()} onClick={handleSubmit}>
                {creating ? "⏳ Копирование..." : "📋 Создать копию"}
              </button>
            ) : (
              <button className="btn-primary" style={{ width: "auto", padding: "13px 40px" }} disabled={!canProceed()} onClick={() => setStep(3)}>
                Далее → Задача
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══ STEP 3: ЗАДАЧА (только создание) ═══ */}
      {!isEditing && !isDuplicating && step === 3 && (
        <div>
          <div className="section-card" style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", userSelect: "none", padding: "4px 0" }}>
              <input type="checkbox" checked={createIntentionsNow} onChange={e => { setCreateIntentionsNow(e.target.checked); if (!e.target.checked) setSelectedTaskId(null); }} style={{ width: 20, height: 20, cursor: "pointer", marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Создать намерения (intentions) сразу</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Результаты выбранной задачи станут целями для аутрича</div>
              </div>
            </label>
          </div>

          {createIntentionsNow && (
            <>
              <div className="section-card" style={{ marginBottom: 16 }}>
                <h3>Выберите задачу-источник</h3>
                <div className="form-group">
                  <label className="form-label">Задача</label>
                  <select className="form-input" style={{ paddingLeft: 14 }} value={selectedTaskId || ""} onChange={e => setSelectedTaskId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">{loadingTasks ? "Загрузка..." : "-- Выберите задачу --"}</option>
                    {tasks.map(t => (
                      <option key={t.id} value={t.id}>{t.name || `#${t.id}`} ({t.task_type})</option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedTaskId && (
                <div className="section-card" style={{ marginBottom: 16 }}>
                  <h3>Информация о задаче</h3>
                  {loadingTaskInfo ? (
                    <div style={{ textAlign: "center", padding: 20 }}><div className="spinner" /></div>
                  ) : taskInfo ? (
                    <div style={{ display: "grid", gap: 12 }}>
                      <div className="info-box"><span>📋</span><div><strong>{taskInfo.name}</strong><p style={{ fontSize: 12 }}>Тип: {taskInfo.task_type}</p></div></div>
                      <div className="info-box" style={{ background: taskInfo.total_results > 0 ? "#f0fdf4" : "#fef2f2", borderColor: taskInfo.total_results > 0 ? "#bbf7d0" : "#fecaca" }}>
                        <span>{taskInfo.total_results > 0 ? "✅" : "⚠️"}</span>
                        <div><strong>{taskInfo.total_results.toLocaleString()} результатов</strong></div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}

          {!createIntentionsNow && (
            <div className="section-card" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ fontSize: 24 }}>💡</span>
                <div>
                  <strong style={{ fontSize: 14, color: "#1e40af" }}>Без намерений</strong>
                  <p style={{ fontSize: 12, color: "#3b82f6", marginTop: 4 }}>Намерения можно добавить позже.</p>
                </div>
              </div>
            </div>
          )}

          <div className="section-card" style={{ marginBottom: 16 }}>
            <h3>📊 Сводка</h3>
            <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
              <div><strong>Название:</strong> {campaignName}</div>
              <div><strong>Платформы:</strong> {selectedPlatforms.length > 0 ? selectedPlatforms.map(p => PLATFORMS.find(pl => pl.key === p)?.label).join(", ") : "не выбраны"}</div>
              <div><strong>Блоков промпта:</strong> {promptBlocks.length}</div>
              {createIntentionsNow && taskInfo && <div><strong>Намерений:</strong> {taskInfo.total_results.toLocaleString()}</div>}
            </div>
          </div>

          <div className="form-actions" style={{ gap: 12 }}>
            <button className="btn-cancel" onClick={() => setStep(2)}>← Назад</button>
            <button className="btn-primary" style={{ width: "auto", padding: "13px 40px" }} disabled={creating || !campaignName.trim()} onClick={handleSubmit}>
              {creating ? "⏳ Создание..." : "🚀 Создать кампанию"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}